/* eslint react/prop-types: 0 */
import React from "react";
import { connect } from "react-redux";
import * as R from "ramda";
import { AddIcon, Div, Text2, Text3, Text4 } from "components/Base";
import BackButton from "./BackButton";
import NextButton from "./NextButton";
import LoginSection from "./LoginSection";
import Field from "./Field";
import MediaQuery from "react-responsive";
import { getters } from "Portal/CallForPapersForm/model";
import { noop } from "utils/General";
import {
  applyRules,
  getSectionDescriptionColor,
  getSectionDescriptionSize,
  getSectionHeaderColor,
  getSectionHeaderSize,
  getSpeakerContactValuesInvalid,
  getEnableAdditionalContacts
} from "../selectors";
import AdditionalContact from "./AdditionalContact";

const Mobile = props => <MediaQuery {...props} maxWidth={820} />;
const Default = props => <MediaQuery {...props} minWidth={821} />;

export const Section = ({ field = null }) => {
  if (field.section_type === "old_header") {
    return (
      <Div className="lennd-valid" mb={4} mt={7}>
        <Div display="row.flex-start" color="black" fw={3} mb={1} fs={5}>
          {field.name}
        </Div>
        {field.description && field.description.length ? (
          <Div
            fs={2}
            color="gray8"
            mb={1}
            style={{
              whiteSpace: "pre-line"
            }}
          >
            {field.description}
          </Div>
        ) : null}
      </Div>
    );
  }

  if (field.section_type === "header") {
    return (
      <Div className="lennd-valid" mb={4} mt={7}>
        <Div display="row.flex-start" color="black" fw={3} mb={1} fs={5}>
          {field.value}
        </Div>
      </Div>
    );
  }

  if (field.section_type === "text") {
    return (
      <div
        style={{
          color: "#000000",
          fontSize: "14px",
          marginBottom: "16px"
        }}
        className="lennd-wysiwyg-content"
      >
        <span
          dangerouslySetInnerHTML={{
            __html: field.value
          }}
        />
      </div>
    );
  }

  if (field.section_type === "divider") {
    return (
      <Div
        bg="gray4"
        width={1}
        style={{
          margin: "30px 0",
          height: "1px"
        }}
      />
    );
  }

  return null;
};

const FieldSection = ({
  disableShowAlert = false,
  handlers = {},
  fieldsChildren = [],
  updateValues = noop,
  updateSpeakerAdditionalContact = noop,
  removeSpeakerAdditionalContact = noop,
  addSpeakerAdditionalContact = noop,
  values = [],
  fields = [],
  additionalContacts = [],
  error = null,
  errorValidation = null,
  wannaCompleteAndIsInvalid = false,
  activeIndex = 0,
  selectedSpeakerId = null,
  enableAdditionalContacts
}) => (
  <Div width={1} bg="neutral0" bra={2} p={5} aria-role="main" tabIndex={0}>
    {fieldsChildren}

    {fields &&
      fields.map(field => {
        const renderField = applyRules(field, fields, values);
        if (!renderField) {
          return "";
        }
        return field.type === "section" ? (
          <Section key={field.id} field={field} />
        ) : (
          <Field
            key={field.id}
            field={field}
            updateValues={updateValues}
            values={values}
          />
        );
      }, fields)}
    {error && error.length ? (
      <Div color="danger8" p={3} bra={1} mt={3} textAlign="center">
        {error}
      </Div>
    ) : null}

    {enableAdditionalContacts && activeIndex === 3 ? (
      <Div
        my={4}
        py={4}
        style={{
          borderTop: "1px solid #E0E0E0",
          borderBottom: "1px solid #E0E0E0"
        }}
        onClick={() => {}}
      >
        {additionalContacts.length > 0 && (
          <Div mb={4}>
            <Text4 bold mb={4}>
              Secondary Contacts
            </Text4>
            {additionalContacts.map((contact, contactIndex) => (
              <AdditionalContact
                generalError={
                  !R.isEmpty(errorValidation) &&
                  !R.isNil(errorValidation) &&
                  wannaCompleteAndIsInvalid
                }
                contact={contact}
                contactIndex={contactIndex}
                selectedSpeakerId={selectedSpeakerId}
                updateSpeakerAdditionalContact={updateSpeakerAdditionalContact}
                removeSpeakerAdditionalContact={removeSpeakerAdditionalContact}
              />
            ))}
          </Div>
        )}
        <Div
          width={1}
          onClick={() => addSpeakerAdditionalContact({ selectedSpeakerId })}
        >
          <Div display="row.flex-start.center">
            <AddIcon color="primary7" mr={1} />
            <Text3 bold color="primary7">
              Add Secondary Contact
            </Text3>
          </Div>
          <Text2 mt={1} color="#666666">
            Secondary contacts can assist speakers with tasks and communication.
          </Text2>
        </Div>
      </Div>
    ) : null}

    {!R.isEmpty(errorValidation) &&
      !R.isNil(errorValidation) &&
      wannaCompleteAndIsInvalid && (
        <Div
          color="danger8"
          p={3}
          bra={1}
          mt={3}
          textAlign="center"
          fs={2}
          fw={3}
        >
          You must complete all mandatory fields to submit this form
        </Div>
      )}

    <Div display="row.space-between.center" mt={5} aria-role="footer">
      <BackButton />
      <NextButton disableShowAlert={disableShowAlert} handlers={handlers} />
    </Div>
  </Div>
);

const decorate = connect(state => ({
  isLoggedIn: getters.isLoggedIn(state),
  activeIndex: getters.activeIndex(state),
  errorValidation: getSpeakerContactValuesInvalid(state),
  wannaCompleteAndIsInvalid: getters.wannaCompleteAndIsInvalid(state),
  sectionHeaderSize: getSectionHeaderSize(state),
  sectionHeaderColor: getSectionHeaderColor(state),
  sectionDescriptionSize: getSectionDescriptionSize(state),
  sectionDescriptionColor: getSectionDescriptionColor(state),
  selectedSpeakerId: getters.selectedSpeakerId(state),
  enableAdditionalContacts: getEnableAdditionalContacts(state)
}));

const InputFields = ({
  disableShowAlert = false,
  handlers = {},
  updateValues = noop,
  updateSpeakerAdditionalContact = noop,
  removeSpeakerAdditionalContact = noop,
  addSpeakerAdditionalContact = noop,
  values = [],
  additionalContacts = [],
  fields = [],
  error = null,
  data = null,
  children = React.Fragment,
  activeIndex = 0,
  errorValidation = [],
  wannaCompleteAndIsInvalid = false,
  sectionHeaderSize,
  sectionHeaderColor,
  sectionDescriptionSize,
  sectionDescriptionColor,
  selectedSpeakerId = null,
  enableAdditionalContacts,
  isExternalSpeaker
}) => (
  <Div>
    <Div mb={6}>
      <h1
        tabIndex={0}
        style={{ color: sectionHeaderColor, fontSize: sectionHeaderSize }}
      >
        {R.propOr("", "title", data)}
      </h1>
      {R.propOr(false, "show_description", data) && (
        <Text3
          mt={1}
          style={{
            whiteSpace: "pre-line",
            color: sectionDescriptionColor,
            fontSize: sectionDescriptionSize
          }}
        >
          {R.propOr("", "description", data)}
        </Text3>
      )}
    </Div>
    <Mobile>
      <Div width={1}>
        <FieldSection
          disableShowAlert={disableShowAlert}
          handlers={handlers}
          fieldsChildren={children}
          updateValues={updateValues}
          values={values}
          fields={fields}
          error={error}
          errorValidation={errorValidation}
          wannaCompleteAndIsInvalid={wannaCompleteAndIsInvalid}
          selectedSpeakerId={selectedSpeakerId}
          updateSpeakerAdditionalContact={updateSpeakerAdditionalContact}
          removeSpeakerAdditionalContact={removeSpeakerAdditionalContact}
          addSpeakerAdditionalContact={addSpeakerAdditionalContact}
          additionalContacts={additionalContacts}
          activeIndex={activeIndex}
          enableAdditionalContacts={
            isExternalSpeaker ? false : enableAdditionalContacts
          }
        />
        {activeIndex !== 3 ||
          (activeIndex !== 4 && <LoginSection handlers={handlers} mobile />)}
      </Div>
    </Mobile>
    <Default>
      <Div display="row">
        <FieldSection
          disableShowAlert={disableShowAlert}
          handlers={handlers}
          fieldsChildren={children}
          updateValues={updateValues}
          selectedSpeakerId={selectedSpeakerId}
          updateSpeakerAdditionalContact={updateSpeakerAdditionalContact}
          removeSpeakerAdditionalContact={removeSpeakerAdditionalContact}
          addSpeakerAdditionalContact={addSpeakerAdditionalContact}
          additionalContacts={additionalContacts}
          activeIndex={activeIndex}
          values={values}
          fields={fields}
          error={error}
          errorValidation={errorValidation}
          wannaCompleteAndIsInvalid={wannaCompleteAndIsInvalid}
          enableAdditionalContacts={
            isExternalSpeaker ? false : enableAdditionalContacts
          }
        />
        {activeIndex !== 3 ||
          (activeIndex !== 4 && <LoginSection handlers={handlers} mobile />)}
      </Div>
    </Default>
  </Div>
);

export default decorate(InputFields);
